import { signal } from '@preact/signals';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { listFiles, type Entity } from '../api.js';
import { t } from '../i18n.js';
import { urlFor } from '../thumbs.js';
import { useDialog } from './dialog.js';
import { IconButton } from './parts.js';
import { IconClose, IconNoImage } from './icons.js';

/**
 * **Choisir une photo de son espace, depuis l'éditeur de notes** *(N2,
 * 2026-08-17)*.
 *
 * C'est le geste que le plan appelait « le sélecteur existant » : il n'existait
 * pas. `CollectionPicker` choisit une **audience** — un groupe, puis un partage —,
 * et rien du site ne savait choisir un **objet**. La grille de la photothèque, qui
 * le saurait, est virtualisée, filtrée, triée et branchée sur la sélection
 * multiple : l'ouvrir en surcouche modale par-dessus l'éditeur aurait fait
 * dépendre l'insertion d'une image de l'écran qu'on regardait derrière.
 *
 * Trois règles, et chacune évite un piège précis :
 *
 * ① **Il demande sa propre liste**, sans toucher aux signaux de la galerie.
 *    Réutiliser `entities` aurait proposé le contenu du dossier courant — depuis
 *    l'éditeur, c'est `/Notes`, où il n'y a aucune photo.
 * ② **Il ne montre que ce qui est affichable** : des images `READY`. Une entité
 *    `PROCESSING` n'a pas encore de dérivé, et une vidéo n'entre pas dans le
 *    sous-ensemble Markdown de `notes.md` §1 — l'offrir ferait insérer une image
 *    qui ne s'affichera jamais.
 * ③ **Il rend un `fid` et un nom**, jamais une URL. La forme canonique du fichier
 *    est `synkara://media/<fid>` : une URL présignée écrite dans une note serait
 *    morte en quinze minutes, et illisible depuis un autre appareil.
 */

/** Ouvert = on sait quoi faire du choix. C'est l'éditeur qui le dit. */
const request = signal<((chosen: { fid: string; name: string }) => void) | undefined>(undefined);

export function openMediaPicker(onPick: (chosen: { fid: string; name: string }) => void): void {
  request.value = onPick;
}

const closeMediaPicker = (): void => void (request.value = undefined);

/** Assez pour couvrir un espace ordinaire, assez peu pour tenir en un appel. */
const LIMIT = 200;

export function MediaPicker(): JSX.Element | null {
  const onPick = request.value;
  const [items, setItems] = useState<readonly Entity[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const host = useDialog(onPick !== undefined, { onEscape: closeMediaPicker });

  const opened = onPick !== undefined;
  useEffect(() => {
    if (!opened) return;
    let alive = true;
    // **On relit à chaque ouverture**, sans mémoriser : une photo envoyée depuis
    // l'éditeur lui-même doit apparaître à la réouverture, et le seul moyen de le
    // garantir est de ne rien garder.
    setItems(undefined);
    setFailed(false);
    void listFiles({ type: 'MEDIA', limit: LIMIT })
      .then((page) => {
        if (!alive) return;
        setItems(page.items.filter((e) => e.Kind === 'IMG' && e.State === 'READY'));
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [opened]);

  if (!onPick) return null;

  const choose = (entity: Entity): void => {
    const fid = entity.Fid;
    if (!fid) return;
    closeMediaPicker();
    // Le nom du fichier fait un texte de remplacement honnête à défaut d'un bon :
    // il dit *quelle* photo, ce que « image » ne dit pas. Il s'édite ensuite dans
    // la barre contextuelle.
    onPick({ fid, name: entity.Name });
  };

  return (
    <div
      class="overlay"
      ref={host}
      tabIndex={-1}
      data-testid="media-picker"
      role="dialog"
      aria-modal="true"
      aria-label={t('editor.imageFromSpace')}
    >
      <div class="sheet">
        <div class="sheet-head">
          <h2>{t('editor.imageFromSpace')}</h2>
          <IconButton
            testId="media-picker-close"
            label={t('viewer.close')}
            onClick={closeMediaPicker}
          >
            <IconClose />
          </IconButton>
        </div>

        {failed ? (
          <p class="notice error" data-testid="media-picker-error" role="alert">
            {t('editor.imagePickFailed')}
          </p>
        ) : items === undefined ? (
          <p class="notice busy" data-testid="media-picker-busy" role="status">
            <span class="spinner" aria-hidden="true" />
            {t('editor.imageLoading')}
          </p>
        ) : items.length === 0 ? (
          <p class="muted" data-testid="media-picker-empty">
            {t('editor.imageNone')}
          </p>
        ) : (
          <ul class="media-picker-grid" data-testid="media-picker-list">
            {items.map((entity) => (
              <li key={entity.Fid}>
                <button
                  type="button"
                  class="media-picker-tile"
                  data-testid={`media-pick-${entity.Fid}`}
                  title={entity.Name}
                  onClick={() => choose(entity)}
                >
                  <Thumb fid={entity.Fid ?? ''} name={entity.Name} />
                  <span class="media-picker-name">{entity.Name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Une vignette. Elle passe par le **lot** de `thumbs.ts` comme la galerie : deux
 * cents tuiles montées dans le même tour de boucle ne font qu'un appel, et c'est
 * précisément ce que la présignature par image a coûté (D-22).
 */
function Thumb({ fid, name }: { readonly fid: string; readonly name: string }): JSX.Element {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    void urlFor(fid, 'thumb')
      .then((signed) => alive && setUrl(signed))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [fid]);

  if (failed) {
    return (
      <span class="media-picker-thumb empty" aria-hidden="true">
        <IconNoImage size={20} />
      </span>
    );
  }
  if (!url) return <span class="media-picker-thumb empty" aria-hidden="true" />;
  return <img class="media-picker-thumb" src={url} alt={name} loading="lazy" />;
}
